import React, { useState } from "react";
import { Button } from "antd";
import {
  CloseCircleOutlined,
  WarningOutlined,
  WifiOutlined,
  QuestionCircleOutlined,
  CopyOutlined,
  CloseOutlined,
  DownOutlined,
  UpOutlined,
} from "@ant-design/icons";
import Logger from "@/utility/logger";

interface ErrorDetails {
  message: string;
  code?: string;
  timestamp?: Date;
  stack?: string;
  context?: Record<string, any>;
}

interface ErrorOverlayProps {
  isVisible: boolean;
  title?: string;
  message: string;
  details?: ErrorDetails;
  type?: string; // "error" | "warning" | "network" | "validation"
  onDismiss?: () => void;
  showRetry?: boolean;
  showReport?: boolean;
  autoHide?: boolean;
  autoHideDelay?: number;
  location: string;
}

function ErrorOverlay({
  isVisible,
  title,
  message,
  details,
  type = "error",
  onDismiss,
  showRetry = false,
  showReport = false,
  autoHide = false,
  autoHideDelay = 5000,
  location,
}: ErrorOverlayProps) {
  const [showDetails, setShowDetails] = useState(false);
  const [isReporting, setIsReporting] = useState(false);

  // Auto-hide functionality
  useState(() => {
    if (autoHide && isVisible) {
      const timer = setTimeout(() => {
        onDismiss?.();
      }, autoHideDelay);
      return () => clearTimeout(timer);
    }
    return undefined;
  });

  const handleRetry = async () => {
    Logger.debug("ErrorOverlay.tsx: Retry not set");
  };

  const handleReport = async () => {
    if (!details) return;

    setIsReporting(true);
    try {
      Logger.debug("ErrorOverlay.tsx: Reporting not set");
      // await onReport(details)
    } finally {
      setIsReporting(false);
    }
  };

  const getErrorIcon = () => {
    switch (type) {
      case "warning":
        return <WarningOutlined />;
      case "network":
        return <WifiOutlined />;
      case "validation":
        return <QuestionCircleOutlined />;
      default:
        return <CloseCircleOutlined style={{ fontSize: "1.75rem" }} />;
    }
  };

  const getErrorColors = () => {
    switch (type) {
      case "warning":
        return {
          bg: "bg-amber-50",
          border: "border-amber-200",
          icon: "text-amber-600",
          title: "text-amber-900",
          text: "text-amber-800",
          button: "bg-amber-600 hover:bg-amber-700",
        };
      case "network":
        return {
          bg: "bg-blue-50",
          border: "border-blue-200",
          icon: "text-blue-600",
          title: "text-blue-900",
          text: "text-blue-800",
          button: "bg-blue-600 hover:bg-blue-700",
        };
      case "validation":
        return {
          bg: "bg-orange-50",
          border: "border-orange-200",
          icon: "text-orange-600",
          title: "text-orange-900",
          text: "text-orange-800",
          button: "bg-orange-600 hover:bg-orange-700",
        };
      default:
        return {
          bg: "bg-red-50",
          border: "border-red-200",
          icon: "text-red-600",
          title: "text-red-900",
          text: "text-red-800",
          button: "bg-red-600 hover:bg-red-700",
        };
    }
  };

  const formatTimestamp = (date: Date) => {
    return date.toLocaleString();
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      Logger.error("ErrorOverlay.tsx: Failed to copy to clipboard:", err);
    }
  };

  const copyErrorDetails = () => {
    if (!details) return;

    const errorInfo = {
      message: details.message,
      code: details.code,
      timestamp: details.timestamp?.toISOString(),
      context: details.context,
      stack: details.stack,
    };

    copyToClipboard(JSON.stringify(errorInfo, null, 2));
  };

  if (!isVisible) return null;

  const colors = getErrorColors();

  return (
    <div className="error-overlay">
      <div className="error-overlay-backdrop" onClick={onDismiss} />

      <div className={`error-overlay-container ${colors.bg} ${colors.border}`}>
        {/* Header */}
        <div className="error-overlay-header">
          <div className={`error-overlay-icon ${colors.icon}`}>{getErrorIcon()}</div>

          <div className="error-overlay-content">
            <h3 className={`error-overlay-title ${colors.title}`}>
              {title ||
                (type === "warning"
                  ? "Warning"
                  : type === "network"
                  ? "Connection Error"
                  : type === "validation"
                  ? "Validation Error"
                  : "Error")}
            </h3>
            <p className={`error-overlay-message ${colors.text}`}>{message}</p>
          </div>

          <button onClick={onDismiss} className="error-overlay-close" aria-label="Close error">
            <CloseOutlined />
          </button>
        </div>

        {/* Details Section */}
        {details && (
          <div className="error-overlay-details-section">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className={`error-overlay-details-toggle ${colors.text}`}
            >
              <span>Show Details</span>
              {showDetails ? <DownOutlined /> : <UpOutlined />}
            </button>

            {showDetails && (
              <div className="error-overlay-details">
                {details.code && (
                  <div className="error-detail-item">
                    <span className="error-detail-label">Error Code:</span>
                    <span className="error-detail-value">{details.code}</span>
                  </div>
                )}

                {details.timestamp && (
                  <div className="error-detail-item">
                    <span className="error-detail-label">Time:</span>
                    <span className="error-detail-value">{formatTimestamp(details.timestamp)}</span>
                  </div>
                )}

                {details.context && Object.keys(details.context).length > 0 && (
                  <div className="error-detail-item">
                    <span className="error-detail-label">Context:</span>
                    <div className="error-detail-context">
                      {Object.entries(details.context).map(([key, value]) => (
                        <div key={key} className="error-context-item">
                          <span className="error-context-key">{key}:</span>
                          <span className="error-context-value">{String(value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {details.stack && (
                  <div className="error-detail-item">
                    <span className="error-detail-label">Stack Trace:</span>
                    <pre className="error-detail-stack">{details.stack}</pre>
                  </div>
                )}

                {location !== "online" && (
                  <button onClick={copyErrorDetails} className="error-copy-button">
                    <CopyOutlined />
                    Copy Details
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="error-overlay-actions">
          {showRetry && (
            <Button onClick={handleRetry} className={`error-action-button ${colors.button}`}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M1 4V10H7"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M3.51 15A9 9 0 0 0 21 12A9 9 0 0 0 5.64 5.64L1 10"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Retry
            </Button>
          )}

          {showReport && details && (
            <Button onClick={handleReport} disabled={isReporting} className="error-report-button bg-transparent">
              {isReporting ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Reporting...
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M14 9V5a3 3 0 0 0-6 0v4"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <rect
                      x="2"
                      y="9"
                      width="20"
                      height="12"
                      rx="2"
                      ry="2"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Report Issue
                </>
              )}
            </Button>
          )}

          <Button onClick={onDismiss} className="error-dismiss-button">
            Dismiss
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ErrorOverlay;
